/* ----------------- React Native --------------- */
import { type CursorValue, type FlexAlignType, Text } from "react-native";

/* ----------------- React Native Paper --------------- */
import { List, type ListItemProps } from "react-native-paper";

/* ----------------- Internal Types --------------- */
import type { Distribution } from "../../types";

/* ----------------- Hooks --------------- */
import { useTranslation } from "react-i18next";
import { useAppTheme } from "../../useAppTheme";

import Icon from "@react-native-vector-icons/material-design-icons/static";
import type { IconType } from "../../icon";
import { useDevice } from "../../useDevice";
/* ----------------- Components --------------- */
import Box from "../Box";
import Button from "../Button";
import CustomChip from "../CustomChip";
import Image from "../CustomImage";
import DonutChart from "../DonutChart";

interface Style {
	marginLeft?: number;
	marginRight?: number;
	marginVertical?: number;
	alignSelf?: FlexAlignType;
}
export type MasteryListItemProps = {
	title: string;
	divider?: boolean;
	header?: boolean;
	distribution: Distribution;
	condensed?: boolean;
	locked?: boolean;
	variant?: "child" | "subchild" | "large";
	rightText?: string;
	tooltip?: string;
	iconUrl?: string;
	actionsBelow?: boolean;
	renderRight?: (p: {
		color: string;
		style?: Style;
	}) => React.ReactNode;
	onPress?: () => void;
	icon?: IconType;
	iconColor?: string;
	score?: number;
	numItems?: number;
	colorScale?: string[];
	id?: number;
	titleChip?: string;
} & Omit<ListItemProps, "id">;

const getRadius = (variant?: "child" | "subchild" | "large") => {
	switch (variant) {
		case "child":
			return {
				innerRadius: 10,
				radius: 14,
			};
		case "subchild":
			return {
				innerRadius: 0,
				radius: 12,
			};
		case "large":
			return {
				innerRadius: 22,
				radius: 28,
			};
		default:
			return {
				innerRadius: 22,
				radius: 28,
			};
	}
};
export const MasteryListItem = ({
	title,
	titleChip,
	distribution,
	titleNumberOfLines = 2,
	header,
	disabled,
	locked,
	variant,
	description,
	rightText,
	onPress,
	icon,
	iconColor,
	iconUrl,
	renderRight,
	score,
	numItems = 0,
	colorScale,
	actionsBelow,
}: MasteryListItemProps) => {
	const { innerRadius, radius } = getRadius(variant);
	const theme = useAppTheme();
	const { t } = useTranslation();
	const { isMobile } = useDevice();
	const showBelow = actionsBelow || isMobile;

	const renderLeft = (p: { color: string; style: Style }) => {
		return (
			<List.Icon
				{...p}
				// biome-ignore lint/complexity/noExcessiveCognitiveComplexity: <explanation>
				icon={() => {
					return (
						<DonutChart
							distribution={
								score != null ? [score, numItems - score] : distribution
							}
							innerRadius={header ? 10 : innerRadius}
							radius={header ? 18 : radius}
							variant={score != null ? "progress" : "mastery"}
							score={score}
							colorScale={colorScale}
						>
							{iconUrl ? (
								<Image
									source={{
										uri: iconUrl,
									}}
									style={{
										width: 36,
										height: 36,
									}}
								/>
							) : (
								icon && (
									<Icon
										name={icon}
										size={24}
										color={iconColor ?? theme.colors.onSurfaceVariant}
									/>
								)
							)}
						</DonutChart>
					);
				}}
			/>
		);
	};

	const actionButton = locked ? (
		<Button
			mode="contained-tonal"
			icon="lock-open-outline"
			buttonColor={theme.colors.tertiaryContainer}
			textColor={theme.colors.onTertiaryContainer}
			onPress={onPress}
		>
			{t("common.unlock")}
		</Button>
	) : (
		<Button
			mode="outlined"
			icon="play-circle-outline"
			textColor={theme.colors.onPrimary}
			buttonColor={theme.colors.primary}
			onPress={onPress}
		>
			{t("dashboard.skills.startCta")}
		</Button>
	);

	const handleRenderRight = (p: { color: string; style?: Style }) => {
		if (!showBelow && rightText) {
			const chipEl = (
				<CustomChip style={{ ...(p.style || {}) }}>{rightText}</CustomChip>
			);
			return (
				<Box flexDirection="row" alignItems="center" gap={8}>
					{chipEl}
					{actionButton}
				</Box>
			);
		}
		return <List.Icon {...p} icon={() => actionButton} />;
	};

	const baseDescription = locked
		? t("dashboard.skills.unlockSubtitle")
		: description;

	const renderDescriptionBelow = () => (
		<Box gap={4} marginTop={4}>
			{typeof baseDescription === "string" && !!baseDescription && (
				<Text
					style={[
						theme.fonts.bodySmall,
						{ color: theme.colors.onSurfaceVariant },
					]}
				>
					{baseDescription}
				</Text>
			)}
			{rightText && (
				<CustomChip style={{ alignSelf: "flex-start" }}>{rightText}</CustomChip>
			)}
		</Box>
	);

	return (
		<List.Item
			style={{
				cursor: onPress ? "pointer" : ("default" as unknown as CursorValue),
				paddingRight: 16,
			}}
			onPress={onPress}
			title={
				<>
					{title}
					{titleChip && !isMobile ? (
						<CustomChip style={{ marginLeft: 8 }}>{titleChip}</CustomChip>
					) : null}
					{locked ? (
						<>
							{" "}
							<Icon name="star" size={14} color={theme.colors.tertiary} />
						</>
					) : null}
				</>
			}
			titleEllipsizeMode="tail"
			titleStyle={
				variant === "large" ? theme.fonts.bodyLarge : theme.fonts.bodyMedium
			}
			titleNumberOfLines={titleNumberOfLines}
			description={showBelow ? renderDescriptionBelow : baseDescription}
			descriptionStyle={showBelow ? undefined : theme.fonts.bodySmall}
			disabled={disabled}
			left={renderLeft}
			right={renderRight ?? handleRenderRight}
		/>
	);
};
